import { Box, Button, Flex, Grid, Heading, Text } from '@chakra-ui/react';
import Link from 'next/link';
import { FC, HTMLAttributeAnchorTarget, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ICBlackDocument from '@/assets/icons/ic-black-document.svg';
import ICDocument from '@/assets/icons/ic-document.svg';
import ICGlobe from '@/assets/icons/ic-globe.svg';
import ICPoster from '@/assets/icons/ic-poster.svg';
import ICSlides from '@/assets/icons/ic-slides-download.svg';
import ICVideo from '@/assets/icons/ic-video.svg';

import { ProfilePopupNew } from '@/components/ui/shared/profile/ProfilePopup/ProfilePopupNew';
import { ThinLecture } from '@/resources/ThinLecture';
import { ThinProfile } from '@/resources/ThinProfile';

interface ICompactLectureCard {
  lecture: ThinLecture;
  time: string | null;
  url: string;
}

export const CompactLectureCardEnabled: FC<ICompactLectureCard> = ({ lecture, time, url }) => {
  const { t } = useTranslation();
  const [profileSelected, setProfileSelected] = useState<null | ThinProfile>(null);

  function handleProfileClick(e: React.MouseEvent, profile: ThinProfile) {
    e.preventDefault();
    e.stopPropagation();
    setProfileSelected(profile);
  }

  return (
    <Link href={url}>
      <Flex _hover={{ bgColor: 'white' }} borderRadius={6} p={4}>
        <Flex
          w="full"
          alignItems={{ base: 'flex-start', md: 'center' }}
          flexDirection={{ base: 'column', md: 'row' }}
          gap={4}
        >
          <Flex w="full" flexDir="column" h="full" justifyContent="flex-start" gap={4}>
            <Heading fontSize="lg" _hover={{ textDecoration: 'underline' }}>
              {lecture.title}
            </Heading>
            {lecture.positionSortedProfiles?.length > 0 && (
              <Box>
                {lecture.positionSortedProfiles.map((profile, index) => (
                  <Button
                    key={profile.id}
                    variant="link"
                    onClick={(e) => handleProfileClick(e, profile)}
                    color="black"
                    fontWeight="normal"
                    _focus={{ boxShadow: 'none' }}
                    mr={1}
                  >
                    {profile.fullName}
                    {index < lecture.positionSortedProfiles.length - 1 && ', '}
                  </Button>
                ))}
              </Box>
            )}
            {profileSelected && (
              <ProfilePopupNew
                isOpen={Boolean(profileSelected)}
                onClose={() => setProfileSelected(null)}
                profiles={[profileSelected]}
                defaultSelectedProfile={profileSelected}
              />
            )}
            {time && (
              <Text as="span" fontSize="sm">
                <Text as="span" fontSize="sm" fontWeight="bold">
                  TIME:{' '}
                </Text>
                <Text as="span">{time}</Text>
              </Text>
            )}
          </Flex>
          <Grid
            gap={4}
            gridTemplateColumns={{ base: 'repeat(4, 70px)', md: 'repeat(2, 70px)', lg: 'repeat(3, 70px)' }}
            gridTemplateRows={{ base: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(2, 1fr)' }}
          >
            {lecture.abstract && (
              <CompactIconButton url={`${url}?tab=${t('lectureTabs.abstractTab')}`} label="abstract">
                <ICBlackDocument />
              </CompactIconButton>
            )}
            {lecture.playlist && (
              <CompactIconButton url={`${url}?tab=${t('lectureTabs.videoTab')}`} label="video">
                <ICVideo />
              </CompactIconButton>
            )}
            {lecture.posterDocument && (
              <CompactIconButton url={`${url}?tab=${t('lectureTabs.posterTab')}`} label="poster">
                <ICPoster />
              </CompactIconButton>
            )}
            {lecture.slideshow && (
              <CompactIconButton url={lecture.slideshowUrl || url} label="slides" target="_blank">
                <ICSlides />
              </CompactIconButton>
            )}
            {(lecture.paper || lecture.paperLink) && (
              <CompactIconButton url={lecture.paperUrl || lecture.paperLink || url} label="paper" target="_blank">
                <ICDocument />
              </CompactIconButton>
            )}
            {lecture.gatherTownUrl && (
              <CompactIconButton url={lecture.gatherTownUrl || url} label="gather">
                <ICGlobe />
              </CompactIconButton>
            )}
          </Grid>
        </Flex>
      </Flex>
    </Link>
  );
};

const CompactIconButton: FC<{
  children: ReactNode;
  label: string;
  url: string;
  target?: HTMLAttributeAnchorTarget;
}> = ({ children, label, url, target }) => {
  return (
    <Link onClick={(e) => e.stopPropagation()} href={url} target={target}>
      <Flex flexDir="column">
        <Box w={6} h={6}>
          {children}
        </Box>
        <Text textAlign="left" textTransform="uppercase" mt={1} fontSize="sm" fontWeight="medium">
          {label}
        </Text>
      </Flex>
    </Link>
  );
};
